import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import { HazardAlert } from '../types';

interface HazardMarkerProps {
  hazard: HazardAlert;
  onMarkerClick: (hazard: HazardAlert) => void;
}

const HazardMarker: React.FC<HazardMarkerProps> = ({ hazard, onMarkerClick }) => {
  const getHazardIcon = (type: string, severity: string) => {
    const iconMap: { [key: string]: string } = {
      'roadwork': '🔨',
      'accident': '🚗',
      'flooding': '💧',
      'debris': '⚠️',
      'closure': '🚫',
      'construction': '🚧',
      'other': '⚠️'
    };

    const severityColors: { [key: string]: string } = {
      'low': '#FCD34D',      // yellow
      'medium': '#FB923C',   // orange
      'high': '#EF4444',     // red
      'urgent': '#A855F7'    // purple
    };

    const icon = iconMap[type] || '⚠️';
    const color = severityColors[severity] || '#6B7280';

    return new DivIcon({
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            position: absolute;
            width: 40px;
            height: 40px;
            background-color: ${color};
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            z-index: 1;
          "></div>
          <div style="
            position: absolute;
            font-size: 18px;
            z-index: 2;
          ">${icon}</div>
          <div style="
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid ${color};
            z-index: 1;
          "></div>
        </div>
      `,
      className: 'custom-hazard-marker',
      iconSize: [40, 48],
      iconAnchor: [20, 48],
      popupAnchor: [0, -48]
    });
  };

  const getSeverityBadgeColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-yellow-100 text-yellow-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHazardTypeColor = (type: string) => {
    switch (type) {
      case 'roadwork': return 'bg-orange-100 text-orange-800';
      case 'accident': return 'bg-red-100 text-red-800';
      case 'flooding': return 'bg-blue-100 text-blue-800';
      case 'debris': return 'bg-yellow-100 text-yellow-800';
      case 'closure': return 'bg-purple-100 text-purple-800';
      case 'construction': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Marker
      position={[hazard.coordinate.latitude, hazard.coordinate.longitude]}
      icon={getHazardIcon(hazard.hazardType, hazard.severity)}
      eventHandlers={{
        click: () => onMarkerClick(hazard)
      }}
    >
      <Popup maxWidth={300}>
        <div className="p-3 min-w-[250px]">
          {/* Header */}
          <div className="flex items-center space-x-2 mb-3">
            <div className="text-lg">
              {hazard.hazardType === 'roadwork' && '🔨'}
              {hazard.hazardType === 'accident' && '🚗'}
              {hazard.hazardType === 'flooding' && '💧'}
              {hazard.hazardType === 'debris' && '⚠️'}
              {hazard.hazardType === 'closure' && '🚫'}
              {hazard.hazardType === 'construction' && '🚧'}
              {hazard.hazardType === 'other' && '⚠️'}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-sm">{hazard.title}</h3>
              <div className="flex items-center space-x-1 mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${getHazardTypeColor(hazard.hazardType)}`}>
                  {hazard.hazardType.charAt(0).toUpperCase() + hazard.hazardType.slice(1)}
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${getSeverityBadgeColor(hazard.severity)}`}>
                  {hazard.severity.charAt(0).toUpperCase() + hazard.severity.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {hazard.description}
          </p>

          {/* Details Grid */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Location:</span>
              <span className="text-gray-900 font-medium">{hazard.island}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Status:</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                hazard.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {hazard.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Created:</span>
              <span className="text-gray-900">{formatDate(hazard.createdAt)}</span>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              onMarkerClick(hazard);
            }}
            className="w-full bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded hover:bg-blue-700 transition-colors"
          >
            View Full Details
          </button>
        </div>
      </Popup>
    </Marker>
  );
};

export default HazardMarker;
